/**
 * Live order state for the whole merchant app.
 *
 * One provider, one realtime subscription, shared by everything that needs order
 * data: the sidebar badge, the queue screen, and the dashboard. Subscribing per
 * screen would open several channels to the same rows and let the badge disagree
 * with the queue it is summarising.
 *
 * Realtime events are treated as change *signals*, not as data: each one
 * triggers a refetch through `restaurant_live_queue`, so the client never
 * maintains a second projection of an order that could drift from the server's.
 *
 * Only the live queue lives here. The day's record is a different question — it
 * is the shop's calendar day, closed orders only, paged and searched — so it is
 * read by `useDayOrders` where it is shown, rather than being fetched on every
 * board reload for a view that is usually closed.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import {
  compareQueueOrder,
  isActiveOrder,
  mergeOrders,
  type Order,
} from '@yummy/shared';
import { fetchLiveQueue, subscribeToRestaurantOrders } from '@yummy/db';

import { supabase } from '../lib/supabase';
import { useMerchant } from './auth';

export type ConnectionStatus = 'connecting' | 'live' | 'offline';

interface OrdersState {
  /** Orders still needing attention, in the order they should be worked. */
  queue: Order[];
  loading: boolean;
  /** True only on the very first load, so a refresh does not blank the board. */
  initial: boolean;
  error: string | null;
  connection: ConnectionStatus;
  reload: () => Promise<void>;
  /** Applies a server-returned order without waiting for the realtime echo. */
  applyOrder: (order: Order) => void;
}

const OrdersContext = createContext<OrdersState | null>(null);

/**
 * Reads the live order state.
 *
 * @returns Queue and loading state.
 * @throws Error when called outside {@link OrdersProvider}.
 */
export function useOrders(): OrdersState {
  const value = useContext(OrdersContext);
  if (!value) throw new Error('useOrders must be used inside OrdersProvider');
  return value;
}

/**
 * Loads and subscribes to orders for the active restaurant.
 *
 * @param props Children.
 */
export function OrdersProvider({ children }: { children: ReactNode }) {
  const { restaurant } = useMerchant();
  const restaurantId = restaurant?.id ?? null;

  const [queue, setQueue] = useState<Order[]>([]);
  const [loading, setLoading] = useState(false);
  const [initial, setInitial] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [connection, setConnection] = useState<ConnectionStatus>('connecting');

  // Guards against a slow response from a previously selected restaurant
  // overwriting the current one.
  const generation = useRef(0);

  // Local changes the last snapshot may not contain yet, and how many have been
  // made. A snapshot asked for before a change and applied after it still
  // describes the older world, so applying it whole puts the order back on the
  // board — a handed-over order reappearing for a moment and leaving again when
  // the next snapshot catches up. One order hides it; marking a run of them in
  // succession is where it shows.
  const sequence = useRef(0);
  const local = useRef(new Map<string, { order: Order; seq: number }>());

  const reload = useCallback(async () => {
    if (!restaurantId) {
      setQueue([]);
      setInitial(false);
      return;
    }

    const current = ++generation.current;
    // Read before the request goes out: everything applied before this point is
    // in the answer, and everything after it is not.
    const askedAt = sequence.current;
    setLoading(true);

    try {
      const nextQueue = await fetchLiveQueue(supabase, restaurantId);

      if (current !== generation.current) return;

      // Drop what this snapshot already reflects, and carry the rest over it. The
      // snapshot's other content still lands — another device's work is not thrown
      // away with the one order that moved here.
      const sinceAsked: Order[] = [];
      for (const [id, entry] of local.current) {
        if (entry.seq <= askedAt) local.current.delete(id);
        else sinceAsked.push(entry.order);
      }

      setQueue(mergeOrders(nextQueue, sinceAsked, compareQueueOrder, isActiveOrder));
      setError(null);
    } catch (cause) {
      if (current !== generation.current) return;
      setError(cause instanceof Error ? cause.message : String(cause));
    } finally {
      if (current === generation.current) {
        setLoading(false);
        setInitial(false);
      }
    }
  }, [restaurantId]);

  useEffect(() => {
    setInitial(true);
    void reload();
  }, [reload]);

  // ---------------------------------------------------------------------------
  // Realtime
  // ---------------------------------------------------------------------------

  useEffect(() => {
    if (!restaurantId) return;

    let timer: ReturnType<typeof setTimeout> | null = null;

    // A single status change produces a row event plus an event-row insert, and
    // a busy service produces bursts. Debouncing collapses each burst into one
    // refetch.
    const schedule = () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => void reload(), 200);
    };

    const unsubscribe = subscribeToRestaurantOrders(supabase, restaurantId, {
      onChange: schedule,
      onStatus: (status) => {
        if (status === 'connected') setConnection('live');
        else if (status === 'error') setConnection('offline');
        else setConnection('connecting');
      },
    });

    return () => {
      if (timer) clearTimeout(timer);
      unsubscribe();
    };
  }, [restaurantId, reload]);

  // If the websocket drops, keep the board fresh on a timer so a busy counter is
  // never looking at stale data. Ten seconds is a compromise between staleness
  // and hammering the API.
  useEffect(() => {
    if (!restaurantId || connection !== 'offline') return;
    const id = setInterval(() => void reload(), 10_000);
    return () => clearInterval(id);
  }, [restaurantId, connection, reload]);

  const applyOrder = useCallback((order: Order) => {
    sequence.current += 1;
    local.current.set(order.id, { order, seq: sequence.current });

    setQueue((current) =>
      mergeOrders(current.filter((o) => o.id !== order.id), [order], compareQueueOrder, isActiveOrder)
    );
  }, []);

  const value = useMemo<OrdersState>(
    () => ({ queue, loading, initial, error, connection, reload, applyOrder }),
    [queue, loading, initial, error, connection, reload, applyOrder],
  );

  return <OrdersContext.Provider value={value}>{children}</OrdersContext.Provider>;
}
